import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { 
  commissionModel, 
  createCommission, 
  validateCommission, 
  formatCommissionForDisplay,
  calculateCommissionSummary,
  filterCommissions
} from '../data/commissionModel';

// CRUD Operations for Commissions

// Create a new commission record
export const createCommissionDocument = async (bookingData, userRole = 'customer') => {
  try {
    console.log('💰 Creating commission document');
    console.log('📊 Booking data:', bookingData);
    console.log('👤 User role:', userRole);
    
    const commission = createCommission(bookingData, userRole);
    console.log('📋 Created commission object:', commission);
    
    const validation = validateCommission(commission);
    console.log('✅ Validation result:', validation);
    
    if (!validation.isValid) {
      console.log('❌ Validation failed:', validation.errors);
      throw new Error(`Validation failed: ${Object.values(validation.errors).join(', ')}`);
    }
    
    console.log('💾 Saving to Firestore...');
    const docRef = await addDoc(collection(db, commissionModel.collection), commission);
    
    console.log('✅ Commission created successfully with ID:', docRef.id);
    return {
      success: true,
      id: docRef.id,
      data: commission
    };
    
  } catch (error) {
    console.error('❌ Error creating commission:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get all commissions
export const getAllCommissions = async () => {
  try {
    console.log('📖 Fetching all commissions...');
    
    const q = query(
      collection(db, commissionModel.collection),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const commissions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('✅ Fetched commissions:', commissions.length);
    return {
      success: true,
      data: commissions
    };
    
  } catch (error) {
    console.error('❌ Error fetching commissions:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Get commission by ID
export const getCommissionById = async (commissionId) => {
  try {
    console.log('📖 Fetching commission by ID:', commissionId);
    
    const docRef = doc(db, commissionModel.collection, commissionId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const commission = {
        id: docSnap.id,
        ...docSnap.data()
      };
      
      console.log('✅ Commission found:', commission.id);
      return {
        success: true,
        data: commission
      };
    } else {
      console.log('❌ Commission not found');
      return {
        success: false,
        error: 'Commission not found'
      };
    }
    
  } catch (error) {
    console.error('❌ Error fetching commission:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get commissions by user ID
export const getCommissionsByUserId = async (userId) => {
  try {
    console.log('📖 Fetching commissions for user:', userId);
    
    const q = query(
      collection(db, commissionModel.collection),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const commissions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('✅ Fetched user commissions:', commissions.length);
    return {
      success: true,
      data: commissions
    };
    
  } catch (error) {
    console.error('❌ Error fetching user commissions:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Get commissions by booking ID
export const getCommissionsByBookingId = async (bookingId) => {
  try {
    console.log('📖 Fetching commissions for booking:', bookingId);
    
    const q = query(
      collection(db, commissionModel.collection),
      where('bookingId', '==', bookingId),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      const commission = {
        id: doc.id,
        ...doc.data()
      };
      
      console.log('✅ Commission found for booking:', bookingId);
      return {
        success: true,
        data: commission
      };
    } else {
      console.log('❌ Commission not found for booking');
      return {
        success: false,
        error: 'Commission not found for booking'
      };
    }
    
  } catch (error) {
    console.error('❌ Error fetching commission by booking:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Update commission
export const updateCommissionDocument = async (commissionId, updateData, processedBy) => {
  try {
    console.log('🔄 Updating commission:', commissionId);
    console.log('👤 Processed by:', processedBy);
    console.log('📝 Update data:', updateData);
    
    const updatePayload = {
      ...updateData,
      updatedAt: serverTimestamp(),
      processedBy: processedBy
    };
    
    // Validate the updated data
    const currentDoc = await getCommissionById(commissionId);
    if (!currentDoc.success) {
      throw new Error('Commission not found');
    }
    
    const updatedCommission = {
      ...currentDoc.data,
      ...updatePayload
    };
    
    const validation = validateCommission(updatedCommission);
    if (!validation.isValid) {
      console.log('❌ Validation failed:', validation.errors);
      throw new Error(`Validation failed: ${Object.values(validation.errors).join(', ')}`);
    }
    
    console.log('💾 Updating Firestore document...');
    const docRef = doc(db, commissionModel.collection, commissionId);
    await updateDoc(docRef, updatePayload);
    
    console.log('✅ Commission updated successfully');
    return {
      success: true,
      data: updatedCommission
    };
    
  } catch (error) {
    console.error('❌ Error updating commission:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Delete commission
export const deleteCommissionDocument = async (commissionId) => {
  try {
    console.log('🗑️ Deleting commission:', commissionId);
    
    const docRef = doc(db, commissionModel.collection, commissionId);
    await deleteDoc(docRef);
    
    console.log('✅ Commission deleted successfully');
    return {
      success: true
    };
    
  } catch (error) {
    console.error('❌ Error deleting commission:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get commission statistics
export const getCommissionStatistics = async (filters = {}) => {
  try {
    console.log('📊 Fetching commission statistics...');
    
    const result = await getAllCommissions();
    if (!result.success) {
      throw new Error(result.error);
    }
    
    let commissions = result.data;
    
    // Apply filters if provided
    if (Object.keys(filters).length > 0) {
      commissions = filterCommissions(commissions, filters);
    }
    
    const summary = calculateCommissionSummary(commissions);
    
    console.log('✅ Commission statistics calculated:', summary);
    return {
      success: true,
      data: summary
    };
    
  } catch (error) {
    console.error('❌ Error calculating commission statistics:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get commissions by date range
export const getCommissionsByDateRange = async (startDate, endDate) => {
  try {
    console.log('📅 Fetching commissions by date range:', startDate, 'to', endDate);
    
    const q = query(
      collection(db, commissionModel.collection),
      where('createdAt', '>=', startDate),
      where('createdAt', '<=', endDate),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const commissions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('✅ Fetched commissions by date range:', commissions.length);
    return {
      success: true,
      data: commissions
    };
    
  } catch (error) {
    console.error('❌ Error fetching commissions by date range:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Get pending commissions
export const getPendingCommissions = async () => {
  try {
    console.log('⏳ Fetching pending commissions...');
    
    const q = query(
      collection(db, commissionModel.collection),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc')
    );
    
    const snapshot = await getDocs(q);
    const commissions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('✅ Fetched pending commissions:', commissions.length);
    return {
      success: true,
      data: commissions
    };
    
  } catch (error) {
    console.error('❌ Error fetching pending commissions:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Confirm commission
export const confirmCommission = async (commissionId, processedBy) => {
  try {
    console.log('✅ Confirming commission:', commissionId);
    
    const updateData = {
      status: 'confirmed',
      paymentStatus: 'processing'
    };
    
    const result = await updateCommissionDocument(commissionId, updateData, processedBy);
    
    if (result.success) {
      console.log('✅ Commission confirmed successfully');
    }
    
    return result;
    
  } catch (error) {
    console.error('❌ Error confirming commission:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Mark commission as paid
export const markCommissionAsPaid = async (commissionId, processedBy, paymentDate = null) => {
  try {
    console.log('💳 Marking commission as paid:', commissionId);
    
    const updateData = {
      status: 'paid',
      paymentStatus: 'paid',
      paymentDate: paymentDate || new Date().toISOString()
    };
    
    const result = await updateCommissionDocument(commissionId, updateData, processedBy);
    
    if (result.success) {
      console.log('✅ Commission marked as paid successfully');
    }
    
    return result;
    
  } catch (error) {
    console.error('❌ Error marking commission as paid:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Cancel commission
export const cancelCommission = async (commissionId, processedBy, reason = '') => {
  try {
    console.log('❌ Cancelling commission:', commissionId);
    
    const updateData = {
      status: 'cancelled',
      paymentStatus: 'unpaid',
      notes: reason ? `Cancelled: ${reason}` : 'Cancelled by admin'
    };
    
    const result = await updateCommissionDocument(commissionId, updateData, processedBy);
    
    if (result.success) {
      console.log('✅ Commission cancelled successfully');
    }
    
    return result;
    
  } catch (error) {
    console.error('❌ Error cancelling commission:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Bulk update commissions
export const bulkUpdateCommissions = async (commissionIds, updateData, processedBy) => {
  try {
    console.log('🔄 Bulk updating commissions:', commissionIds.length);
    
    const results = [];
    for (const commissionId of commissionIds) {
      const result = await updateCommissionDocument(commissionId, updateData, processedBy);
      results.push({ id: commissionId, ...result });
    }
    
    const successCount = results.filter(r => r.success).length;
    console.log(`✅ Bulk update completed: ${successCount}/${commissionIds.length} successful`);
    
    return {
      success: true,
      results,
      successCount,
      totalCount: commissionIds.length
    };
    
  } catch (error) {
    console.error('❌ Error in bulk update:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

